import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import QualificationModal from '../components/QualificationModal';
import { trackCtaClick } from '../lib/pixel';
import { logger } from '../lib/logger';

const QualificationCtx = createContext({
  open: () => {},
  close: () => {},
});

/**
 * Wraps the public site with a global qualification modal.
 * Any component can call `useQualification().open('hero_button')` to open it.
 */
export function QualificationProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState('unknown');

  const open = useCallback((ctaSource = 'unknown') => {
    // ★ Meta Pixel: every CTA that opens the modal is logged as `CtaClick`.
    // This covers ~15 entry points on the landing (hero, manifesto banner,
    // testimonials, footer, etc.) without touching individual call-sites.
    try {
      trackCtaClick({
        label: 'open_qualification_modal',
        source: ctaSource || 'unknown',
        position: ctaSource || 'unknown',
      });
    } catch (err) { logger.debug('trackCtaClick (qual open) failed', err); }
    setSource(ctaSource);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  // Memoize the value so consumers don't re-render unless `open`/`close` change
  // (they never do — both are useCallback with empty deps).
  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <QualificationCtx.Provider value={value}>
      {children}
      <QualificationModal
        open={isOpen}
        onOpenChange={setIsOpen}
        ctaSource={source}
      />
    </QualificationCtx.Provider>
  );
}

export function useQualification() {
  return useContext(QualificationCtx);
}
